import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import { Store } from '@ngrx/store';
import { RootState } from '@root-store/index';
import { ILogService, IPromise, IQService, isDefined } from 'angular';
import { i18n } from 'i18next';
import moment from 'moment';
import { filter, of, switchMap, withLatestFrom } from 'rxjs';

import {
  COMETD_ALERT_MESSAGE,
  COMETD_INSTRUCTION_REMINDING,
  CSR_FACADE_PREFIX,
  isAgentReady,
  isNotNullOrUndefined,
  MessagingService,
  ToasterFacade,
  ToastType,
  waitToLoad,
} from '@contact/upgrade';

import { Alert, IAlert } from './models/alert';

const MODULE_NAME = 'alert';

export interface IAlertService {}

export class AlertService implements IAlertService {
  static $inject: Array<string> = ['$log', '$q', '$i18next', 'messaging', 'httpDeprecated', 'toasterFacade', 'ngrxStoreService'];

  public name = MODULE_NAME;
  private readonly buildDisplayTime = (alert: Alert): IPromise<string> => {
    const deferred = this.$q.defer<string>();

    if (isDefined(alert) && alert.date) {
      deferred.resolve(moment(alert.date).format('H[h]mm'));
    } else {
      deferred.reject('error on alert buildDisplayTime');
    }

    return deferred.promise;
  };

  private buildAlertMessage = (alert: IAlert): IPromise<string> => {
    const deferred = this.$q.defer<string>();

    let keyPattern: RegExp = /^(\w+)\{\[([\w- .\[\]]+)\]{1}|\[([\w- .\[\]]+)\],|\[([\w- .\[\]]+)\]\}$/g;
    let match: RegExpExecArray | null = keyPattern.exec(alert.message);
    let shortKey: string = alert.message;
    let paramValues: any = {};
    let paramNb: number = 0;

    while (match != null) {
      shortKey = match[1] != null ? match[1] : shortKey;
      for (let i = 2; i < match.length; i++) {
        if (match[i] != null) {
          paramValues[paramNb] = match[i];
          paramNb++;
        }
      }
      match = keyPattern.exec(alert.message);
    }

    if (isDefined(shortKey)) {
      this.buildDisplayTime(alert).then(
        timeToDisplay => {
          deferred.resolve(timeToDisplay + ' - ' + this.$i18next.t('commons:alert.' + shortKey, paramValues));
        },
        () => {
          deferred.reject('error when building alert time to display');
        }
      );
    } else {
      deferred.reject('Key not found');
    }

    return deferred.promise;
  };
  private handleAlertMessage = (dto: any): void => {
    this.buildAlertMessage(new Alert(dto.message)).then(text => {
      this.toasterFacade.alert({ message: text });
    });
  };

  private getAcquitInstrunction(): void {
    this.httpDeprecated
      .asPromise()
      .get(`${CSR_FACADE_PREFIX}/getAcquitInstructionCsr`)
      .then(result => {
        if (result.data === 'false') {
          return;
        }
        for (let i = 0; i < result.data.length; i++) {
          this.toasterFacade.instruction({
            message: result.data[i].title,
            instruction: {
              id: result.data[i].uuid,
              startDateInMillis: result.data[i].startDate,
              endDateInMillis: result.data[i].endDate,
              title: result.data[i].title,
              message: result.data[i].message,
              severity: result.data[i].severity,
              reminder: result.data[i].reminder,
              endType: result.data[i].endType,
            },
          });
        }
      });
  }

  private acquitInstrunction(instructionUuid: string): void {
    this.httpDeprecated
      .asPromise()
      .withConfig({
        'Content-Type': 'application/json; charset=UTF-8',
      })
      .post(`${CSR_FACADE_PREFIX}/acquitInstruction`, [instructionUuid]);
  }

  private addLogForTracking(instructionId: string): void {
    this.httpDeprecated
      .asPromise()
      .withConfig({
        'Content-Type': 'application/json; charset=UTF-8',
      })
      .post(`${CSR_FACADE_PREFIX}/addLogForTracking` + '/' + instructionId);
  }

  init(): void {
    this.messaging
      .forRemote()
      .listen$(COMETD_ALERT_MESSAGE)
      .subscribe(
        dto => this.handleAlertMessage(dto),
        () => this.$log.error(`Cannot subscribe to[${COMETD_ALERT_MESSAGE}]`)
      );

    // INSTRUCTION_REMINDING_CHANNEL
    this.messaging
      .forRemote()
      .listen$<any>(COMETD_INSTRUCTION_REMINDING)
      .subscribe(dto => {
        this.toasterFacade.instruction({
          message: dto.title,
          instruction: {
            id: dto.uuid,
            startDateInMillis: dto.startDate,
            endDateInMillis: dto.endDate,
            title: dto.title,
            message: dto.message,
            severity: dto.severity,
            reminder: dto.reminder,
            endType: dto.endType,
          },
        });
      });

    this.toasterFacade.close$
      .pipe(
        switchMap(toastObj =>
          of({}).pipe(
            withLatestFrom(this.toasterFacade.getById(toastObj.id.toString()), (_, toast) => toast),
            isNotNullOrUndefined(),
            filter(({ componentName }) => componentName === ToastType.INSTRUCTION)
          )
        )
      )
      .subscribe(({ data }) => {
        this.addLogForTracking((data as any)['id']);
        this.acquitInstrunction((data as any)['id']);
      });

    this.getAcquitInstrunction();
  }

  constructor(
    private readonly $log: ILogService,
    private readonly $q: IQService,
    private readonly $i18next: i18n,
    private readonly messaging: MessagingService,
    private readonly httpDeprecated: HttpDeprecatedService,
    private readonly toasterFacade: ToasterFacade,
    private readonly store$: Store<RootState>
  ) {
    this.store$.pipe(waitToLoad([isAgentReady])).subscribe(_ => this.init());
  }
}

export default MODULE_NAME;
